import { LatLngLiteral } from "leaflet";
import { clone, isEqual } from "lodash";
import { ModelEventMeta } from "@node-elion/syncron";
import * as ModelEvent from "@node-elion/syncron";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import { getPRPC } from "../../hooks/usePRPC";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { Base, Language } from "..";
import { createLogger } from "../../utils";

const logger = createLogger({ name: "PrizeZone" });

interface Subscription<Options> {
	unsubscribe(): Promise<void>;
	update(options: Options): Promise<void>;
}

interface ServiceSubscribeOnUpdateData<Model> {
	models: Model[];
	metadataState: ModelEventMeta;
}

type OnUpdate<Model> = (
	data: ServiceSubscribeOnUpdateData<Model>,
) => void | Promise<void>;

class PriceZone extends Base {
	static fromResponse(data: any): PriceZone.Model {
		const fn = (value) => {
			if (value?.type === "Polygon" && value?.coordinates?.length) {
				const coordinates: any[] = value.coordinates as any[];
				return (
					coordinates?.flat?.()?.map?.((v) => ({
						lat: v?.at?.(-1) || v?.lat,
						lng: v?.at?.(0) || v?.lng,
					})) || []
				);
			}

			if (Array.isArray(value) && value?.length) {
				return (
					value?.flat?.()?.map?.((v) => ({
						lat: v?.lat || v?.at?.(-1),
						lng: v?.lng || v?.at?.(0),
					})) || []
				);
			}

			return [];
		};
		const vertices = fn(data?.vertices);

		return {
			id: data.id,

			taxiServiceId: data.taxiService.id,

			position: new Date(data.position),
			name: data.name,
			vertices,
			active: data.status,
			suburban: data.suburban,
			bySector: data.bySector,

			createdAt: "",
			updatedAt: "",
			deletedAt: null,
		};
	}

	static toRequest(
		model: PriceZone.Model.New | PriceZone.Model.Modified,
	): any {
		const vertices = clone(model.vertices);

		if (vertices && !isEqual(vertices[vertices.length - 1], vertices[0]))
			vertices.push(vertices[0]);

		return {
			taxiServiceId: model.taxiServiceId,

			position: model.position,
			name: model.name,
			vertices,

			status: model.active,
			suburban: model.suburban,
		};
	}

	static async store(object: PriceZone.Model.New) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.priceZone.create(
						PriceZone.toRequest(object),
					),
				{ silent: false, error: true },
			);
			return res;
		} catch (err: any) {
			logger.error("[PrizeZOne] Error update:", err);
			return false;
		}
	}

	static async update(object: PriceZone.Model.Modified) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.priceZone.update(
						object.id,
						PriceZone.toRequest(object),
					),
				{ silent: false, error: true },
			);
			return res;
		} catch (err: any) {
			logger.error("[PrizeZOne] Error update:", err);
			return false;
		}
	}

	static async destroy(id: number[] | number) {
		const prpc = getPRPC();

		if (!prpc) return;

		if (Array.isArray(id))
			await Promise.allSettled(id.map((id) => this.destroyOne(id)));
		else await this.destroyOne(id);
	}

	private static async destroyOne(id: number) {
		this.request((prpc) => prpc.theirsModel.priceZone.delete(id));
	}

	public static async subscribe(
		options: PriceZone.SubscribeOptions,
		onUpdate: OnUpdate<PriceZone.Model>,
	): Promise<Subscription<PriceZone.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				const models = state.models.map((item) =>
					this.fromResponse(item),
				);

				onUpdate({
					...state,
					models,
				});
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.priceZone.subscribe({
					params: {
						limit: options.limit,
						offset: options.offset,
						query: options.query,

						taxiServiceIds: options.taxiServiceIds,

						status: options.active,
						suburban: options.suburban,
						bySector: options.bySector,

						lang: options.language,

						order: options.order,
					},
					ping: () => true,
					onEvent: (event) => {
						modelEventConstructor.onEvent(event);
					},
					onError: (error) => {
						console.error("[PrizeZone] Error subscription:", error);
					},
				}),
			{ name: "PrizeZone.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (opt: PriceZone.SubscribeOptions) =>
				subscription.update({
					limit: opt.limit,
					offset: opt.offset,
					query: opt.query,

					taxiServiceIds: opt.taxiServiceIds,

					status: opt.active,
					suburban: opt.suburban,
					bySector: opt.bySector,

					lang: opt.language,

					order: opt.order,
				}),
		} as Subscription<PriceZone.SubscribeOptions>;
	}
}

declare namespace PriceZone {
	interface Model {
		id: number;

		taxiServiceId: number;

		position: Date | number;
		name: Record<Language, string>;
		vertices: LatLngLiteral[];

		active: boolean;
		suburban?: boolean;
		bySector: boolean;

		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
	}

	interface SubscribeOptions
		extends ServiceSubscribeOptionsBase<PriceZone.Model> {
		language?: Language;
		taxiServiceIds?: number[];

		active?: boolean;
		suburban?: boolean;
		bySector?: boolean;
	}

	interface SharedOptions {
		deprecate?: boolean;
	}

	interface StoreOptions extends SharedOptions {}
	interface UpdateOptions extends SharedOptions {}
	interface DestroyOptions extends SharedOptions {}

	namespace Model {
		type NonMutableOptions =
			| "id"
			| "bySector"
			| "createdAt"
			| "updatedAt"
			| "deletedAt";

		type New = Omit<Model, NonMutableOptions>;
		type Modified = Partial<New> & Pick<Model, "id">;
	}
}

export default PriceZone;
